import { getMemberInfo } from '@/hooks/useMembers';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';
import styles from './ManageSubscription.module.css';
import MHeader from '@/components/ui/organisms/MHeader';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MRadio from '@/components/ui/atoms/MRadio';
import MBadge from '@/components/ui/atoms/MBadge';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import { MButton } from '@/components/ui/atoms/MButtons';
import MDialog from '@/components/ui/atoms/MDialog';
import { formatDocumentDateShort } from '@/utils/date-utils';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/hooks/useSubscription';
import { triggerNativeIAPPurchase, triggerNativeIAPCancel } from '@/utils/nativeBridge';
import Lottie from 'lottie-react';
import successAnimation from '@/assets/animations/success.json';

const ManageSubscription = () => {
  const { t } = useTranslation();
  const memberInfo = getMemberInfo();
  const navigate = useNavigate();
  const { subscription: currentSubscription, plans, loading, fetchPlans, fetchCurrentSubscription } = useSubscription();
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [currentPlanId, setCurrentPlanId] = useState<string>('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lottieLoaded, setLottieLoaded] = useState(false);

  const PLAN_BENEFITS = [
    t('subscription.manageSubscription.benefits.familyAccess'),
    t('subscription.manageSubscription.benefits.privateStorage'),
    t('subscription.manageSubscription.benefits.storeDocuments'),
    t('subscription.manageSubscription.benefits.emergencyCards'),
    t('subscription.manageSubscription.benefits.extensiveStorage')
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchPlans(),
          fetchCurrentSubscription()
        ]);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchData();
  }, [fetchPlans, fetchCurrentSubscription]);

  useEffect(() => {
    // Set the subscribed plan as selected by default
    const subscribedPlan = plans.find(p => p.isSubscribed);
    if (subscribedPlan) {
      setSelectedPlanId(subscribedPlan.id);
      setCurrentPlanId(subscribedPlan.id);
    }
  }, [plans]);

  useEffect(() => {
    // Listen for IAP purchase and cancel results from native
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'iap_purchase_result') {
          console.log('IAP Purchase Result:', data.payload);
          
          if (data.payload.success) {
            // Payment successful - show success modal
            setLottieLoaded(false); // Reset Lottie state
            setShowSuccessModal(true);
          } else {
            // Payment failed - navigate to subscription details page
            // The page will show the failed state with retry option
            navigate('/profile/subscription');
          }
        }
        
        if (data.type === 'iap_cancel_result') {
          console.log('IAP Cancel Result:', data.payload);
          
          if (data.payload.success) {
            // Subscription management opened successfully
            // Navigate to subscription page to show updated status
            navigate('/profile/subscription', { state: { showCancelInfo: true } });
          } else {
            console.error('Failed to open subscription management:', data.payload.error);
          }
        }
      } catch (error) {
        console.error('Error parsing message from native:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as any);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as any);
    };
  }, [navigate]);

  if (loading) {
    return null;
  }

  // Check if user has a trial subscription based on current subscription
  const hasTrialSubscription = currentSubscription?.plan?.type === 'trial';

  const handleCancelSubscription = () => {
    // Trigger native subscription management (App Store/Play Store)
    triggerNativeIAPCancel();
    setShowCancelModal(false);
  };

  const handleContinueToPayment = () => {
    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    if (!selectedPlan || !memberInfo) {
      console.error('No plan selected or member info missing');
      return;
    }

    // Check if Android user with active paid subscription - redirect to Google Play
    const ua = (navigator.userAgent || '').toLowerCase();
    const isAndroid = /android/.test(ua);
    
    if (isAndroid && currentSubscription) {
      const planName = currentSubscription?.plan?.name || '';
      const isFreePlan = planName.toLowerCase().includes('early user') || 
                        planName.toLowerCase().includes('trial');
      
      const isPaidSubscription = currentSubscription?.status === 'active' && !isFreePlan;
      
      if (isPaidSubscription) {
        console.log('[ManageSubscription] Active PAID subscription detected on Android - redirecting to Google Play');
        console.log('[ManageSubscription]   Current plan name:', planName);
        console.log('[ManageSubscription]   Current plan type:', currentSubscription?.plan?.type);
        
        // Open Google Play subscription management for plan changes
        const subscriptionUrl = `https://play.google.com/store/account/subscriptions?sku=maatra_subscription&package=health.maatra.app`;
        
        console.log('[ManageSubscription] Opening subscription management:', subscriptionUrl);
        window.location.href = subscriptionUrl;
        return;
      }
    }

    const product = selectedPlan.products?.[0];
    if (!product) {
      console.error('No product found for selected plan');
      return;
    }

    const payload = {
      memberId: memberInfo.id,
      familyId: memberInfo.current_family,
      planId: selectedPlan.id,
      planName: selectedPlan.name,
      planType: selectedPlan.type,
      productId: product.productId,
      price: product.price,
      currency: 'INR' // Default to INR, can be made dynamic later
    };

    console.log('IAP Purchase Payload:', payload);

    // Trigger native IAP purchase flow
    triggerNativeIAPPurchase(payload);
  };

  // Format the end date for the modal description
  const getCancelModalDescription = () => {
    if (currentSubscription?.endDate) {
      const formattedDate = formatDocumentDateShort(currentSubscription.endDate, true);
      const text = t('subscription.manageSubscription.cancelModal.descriptionWithDate', { date: formattedDate });
      const parts = text.split('\n\n');
      return (
        <>
          {parts[0]}
          <br /><br />
          {parts[1]}
        </>
      );
    }
    const text = t('subscription.manageSubscription.cancelModal.descriptionDefault');
    const parts = text.split('\n\n');
    return (
      <>
        {parts[0]}
        <br /><br />
        {parts[1]}
      </>
    );
  };

  return (
    <div className="profile-page-root">
      <MHeader
        title={t('subscription.manageSubscription.title')}
        module="subscription"
        memberInfo={memberInfo}
        description={t('subscription.manageSubscription.description')}
        learnMoreContent={t('subscription.manageSubscription.learnMoreContent')}
        drawerTitle={t('subscription.manageSubscription.learnMoreTitle')}
      />

      <MSpacer mt={24} />

      <div className={styles.manageSubscription}>
        {/* What you get with a paid plan - only show for trial users */}
        {hasTrialSubscription && (
          <>
            <div className={styles.benefitsCard}>
              <h2 className={styles.benefitsTitle}>{t('subscription.manageSubscription.benefitsTitle')}</h2>
                        
              <div className={styles.benefitsList}>
                {PLAN_BENEFITS.map((benefit, index) => (
                  <div key={index} className={styles.benefitItem}>
                    <IconCircleCheckFilled size={20} color="#45168C" className={styles.checkIcon} />
                    <span className={styles.benefitText}>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <MSpacer mt={32} />
          </>
        )}

        <h2 className={styles.sectionTitle}>{t('subscription.manageSubscription.selectPlan')}</h2>
        
        <MSpacer mt={16} />

        <div className={styles.plansContainer}>
            {plans.filter(p => !p.internalPlan).map((plan) => (
              <div 
                key={plan.id} 
                className={`${styles.planCard} ${selectedPlanId === plan.id ? styles.selected : ''}`}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <div className={styles.planHeader}>
                  <div className={styles.radioWrapper}>
                    <MRadio
                      checked={selectedPlanId === plan.id}
                      onChange={() => setSelectedPlanId(plan.id)}
                    />
                  </div>
                  <div className={styles.planContent}>
                    <div className={styles.planTitle}>{plan.name}</div>
                    {plan.type === 'annual' && (
                      <MBadge className={styles.offerBadge}>
                        <span className={styles.offerText}>{t('subscription.manageSubscription.offerBadge')}</span>
                      </MBadge>
                    )}
                    <div className={styles.priceRow}>
                       {plan.type === 'annual' && (
                        <span className={styles.discountedPrice}>
                          ₹ 2,388
                        </span>
                      )}
                      <span className={styles.price}>
                        ₹ {plan.products[0]?.price || 0}/{plan.type === 'monthly' ? t('subscription.manageSubscription.month') : t('subscription.manageSubscription.year')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <br/>
          {(() => {
          const ua = (navigator.userAgent || '').toLowerCase();
          const isIOS = /iphone|ipad|ipod/.test(ua) && /applewebkit/.test(ua);
          return isIOS && (
            <a 
              href="itms-apps://apps.apple.com/account/subscriptions"
              style={{ textDecoration: 'none' }}
            >
              <MButton 
                variant='link'
                text={t('subscription.manageSubscription.restorePurchases')}
                size='lg'
              />
            </a>
          );
        })()}
      </div>
      <div className={styles.actionButtons}>
  <MButton 
          className={styles.confirmButton} 
          text={t('subscription.manageSubscription.continueToPayment')}
          size='lg'
          onClick={handleContinueToPayment}
          disabled={
            !selectedPlanId || 
            (selectedPlanId === currentPlanId && currentSubscription?.status.toLowerCase() === 'active') ||
            (currentSubscription?.status.toLowerCase() !== 'cancelled' && currentSubscription?.nextPlanId && selectedPlanId === currentSubscription.nextPlanId)
          }
        />
        {currentSubscription?.status.toLowerCase() !== 'cancelled' && !hasTrialSubscription && (
          <MButton 
            variant='link'
            text={t('subscription.manageSubscription.cancelSubscription')}
            size='lg'
            onClick={() => setShowCancelModal(true)}
          />
        )}
      </div>

      {/* Cancel Subscription Modal */}
      <MDialog
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title={t('subscription.manageSubscription.cancelModal.title')}
        description={getCancelModalDescription()}
        primaryCtaText={t('subscription.manageSubscription.cancelModal.confirmButton')}
        secondaryCtaText={t('subscription.manageSubscription.cancelModal.cancelButton')}
        onPrimaryCta={handleCancelSubscription}
        onSecondaryCta={() => setShowCancelModal(false)}
      />

      {/* Success Modal */}
      <MDialog
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/profile/subscription');
        }}
        title=""
        primaryCtaText={t('subscription.manageSubscription.successModal.cta')}
        onPrimaryCta={() => {
          setShowSuccessModal(false);
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.INSTALL,
            screenName: ANALYTICS_SCREEN_NAMES.SUBSCRIPTION_SCREEN_NAME,
            action: ANALYTICS_SCREEN_NAMES.SUBSCRIPTION_ACTION
          });
          navigate('/profile/subscription');
        }}
      >
        <div className={styles.successModalContent}>
          <div className={styles.lottieContainer}>
            <Lottie 
              animationData={successAnimation}
              loop={false}
              autoplay={true}
              style={{ width: 200, height: 200 }}
              initialSegment={[0, 60]}
              onLoad={() => setLottieLoaded(true)}
              onComplete={() => {
                // Animation completed
              }}
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid meet',
                progressiveLoad: true,
                hideOnTransparent: true
              }}
            />
            {!lottieLoaded && (
              <div style={{ 
                position: 'absolute', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: '50%'
              }}>
                <IconCircleCheckFilled size={100} color="white" />
              </div>
            )}
          </div>
          <h2 className={styles.successTitle}>
            {t('subscription.manageSubscription.successModal.title')}
          </h2>
          <p className={styles.successDescription}>
            {t('subscription.manageSubscription.successModal.description')}
          </p>
        </div>
      </MDialog>
       
    </div>
  );
};

export default ManageSubscription;
